import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Check, X, ArrowRight, Sprout, BookOpen, Calendar, DollarSign, Share2, ListChecks, ShieldCheck, Star, ChevronDown, ChevronUp, Leaf, ClipboardList, TrendingUp } from 'lucide-react';
import { CountdownTimer } from './CountdownTimer';
import { SocialProofPopup } from './SocialProofPopup';
// --- Utility Components ---
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} data-id="element-22">
    {children}
  </motion.div>;
const Button = ({
  children,
  variant = 'primary',
  className = '',
  fullWidth = false,
  href
}: {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline';
  className?: string;
  fullWidth?: boolean;
  href?: string;
}) => {
  const baseStyles = 'inline-flex items-center justify-center px-8 py-4 text-lg font-bold tracking-wide transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl rounded-md cursor-pointer';
  const variants = {
    primary: 'bg-forest-800 text-white hover:bg-forest-700 border-2 border-transparent',
    secondary: 'bg-sage-500 text-white hover:bg-sage-600 border-2 border-transparent',
    outline: 'bg-transparent text-forest-800 border-2 border-forest-800 hover:bg-forest-50'
  };
  const handleClick = () => {
    window.open(href || 'https://pagos.mundokrea.com/cart/47466027221044:1', '_blank');
  };
  return <button onClick={handleClick} className={`${baseStyles} ${variants[variant]} ${fullWidth ? 'w-full' : ''} ${className}`} data-id="element-23">
      {children}
    </button>;
};
const SectionHeading = ({
  children,
  subtitle,
  centered = true,
  dark = false
}: {
  children: React.ReactNode;
  subtitle?: string;
  centered?: boolean;
  dark?: boolean;
}) => <div className={`mb-8 ${centered ? 'text-center' : ''}`} data-id="element-24">
    <h2 className={`text-3xl md:text-4xl font-serif font-bold mb-3 ${dark ? 'text-white' : 'text-forest-900'}`} data-id="element-25">
      {children}
    </h2>
    {subtitle && <p className={`text-base md:text-lg max-w-2xl mx-auto ${dark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-26">
        {subtitle}
      </p>}
    <div className={`h-1 w-20 bg-sage-400 mt-4 ${centered ? 'mx-auto' : ''}`} data-id="element-27"></div>
  </div>;
const AccordionItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 last:border-0" data-id="element-28">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-29">
        <span className="text-lg font-serif font-medium text-forest-900 group-hover:text-forest-700 transition-colors" data-id="element-30">
          {question}
        </span>
        {isOpen ? <ChevronUp className="text-sage-500 flex-shrink-0 ml-4" data-id="element-31" /> : <ChevronDown className="text-sage-500 flex-shrink-0 ml-4" data-id="element-32" />}
      </button>
      <motion.div initial={false} animate={{
      height: isOpen ? 'auto' : 0,
      opacity: isOpen ? 1 : 0
    }} transition={{
      duration: 0.3
    }} className="overflow-hidden" data-id="element-33">
        <p className="pb-6 text-gray-600 leading-relaxed" data-id="element-34">{answer}</p>
      </motion.div>
    </div>;
};
// --- Main Page Component ---
export function SalesPage() {
  return <div className="min-h-screen bg-white font-sans text-gray-800 overflow-x-hidden" data-id="element-35">
      <CountdownTimer data-id="element-36" />
      <SocialProofPopup data-id="element-37" />

      {/* 1. HERO SECTION */}
      <section className="relative pt-12 pb-16 px-4 md:pt-20 md:pb-24 overflow-hidden bg-cream-100" data-id="element-38">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-10" data-id="element-39">
          <Leaf className="absolute top-10 left-10 w-64 h-64 text-forest-800 rotate-45" data-id="element-40" />
          <Leaf className="absolute bottom-10 right-10 w-96 h-96 text-sage-600 -rotate-12" data-id="element-41" />
        </div>

        <div className="max-w-6xl mx-auto relative z-10 text-center" data-id="element-42">
          <FadeIn data-id="element-43">
            <span className="inline-block py-1 px-3 rounded-full bg-sage-100 text-forest-800 text-sm font-bold tracking-wider uppercase mb-6 border border-sage-200" data-id="element-44">
              +500 emprendedores ya lo tienen
            </span>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-forest-900 leading-tight mb-8" data-id="element-45">
              Multiplicá Tus Plantas y Generá{' '}
              <span className="text-forest-600 italic" data-id="element-46">Ingresos Reales</span>{' '}
              Desde Tu Casa
            </h1>

            {/* Product Image */}
            <div className="relative max-w-3xl mx-auto mb-8 group cursor-pointer" onClick={() => document.getElementById('comprar')?.scrollIntoView({
            behavior: 'smooth'
          })} data-id="element-47">
              <div className="absolute inset-0 bg-sage-400 blur-3xl opacity-20 rounded-full transform scale-90 group-hover:scale-100 transition-transform duration-700" data-id="element-48"></div>
              <div className="relative transform hover:scale-[1.02] transition-transform duration-500" data-id="element-49">
                <img src="https://cdn.magicpatterns.com/uploads/8DouRbsQ8mzGNSrJYVe1vz/imagen_para_pagina_.png" alt="MEGAPACK Del Esqueje al Negocio - Guía completa para multiplicar plantas y hacer un negocio rentable" className="w-full h-auto rounded-lg drop-shadow-2xl" data-id="element-50" />
              </div>
            </div>

            <p className="text-lg md:text-2xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed" data-id="element-51">
              El sistema completo que te enseña <strong data-id="element-52">paso a paso</strong>{' '}
              cómo multiplicar plantas, organizar tu producción y vender con
              confianza.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* PRICE ANCHOR SECTION - Immediately visible */}
      <section className="py-8 md:py-12 px-4 bg-forest-900 text-white" data-id="element-53">
        <div className="max-w-5xl mx-auto" data-id="element-54">
          <FadeIn data-id="element-55">
            <div className="text-center mb-6" data-id="element-56">
              <p className="text-sage-300 text-sm md:text-base uppercase tracking-wider mb-2" data-id="element-57">
                Oferta especial por tiempo limitado
              </p>
              <h2 className="text-2xl md:text-3xl font-serif font-bold" data-id="element-58">
                Elegí Tu Plan y Empezá Hoy
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4 md:gap-6 max-w-3xl mx-auto" data-id="element-59">
              {/* Basic Price Card */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center hover:bg-white/15 transition-colors" data-id="element-60">
                <h3 className="text-lg font-bold text-sage-300 uppercase tracking-wider mb-3" data-id="element-61">
                  Megapack Básico
                </h3>
                <div className="mb-4" data-id="element-62">
                  <span className="text-gray-400 line-through text-lg block" data-id="element-63">
                    $39.990
                  </span>
                  <div className="flex items-baseline justify-center gap-1" data-id="element-64">
                    <span className="text-4xl md:text-5xl font-serif font-bold text-white" data-id="element-65">
                      $19.990
                    </span>
                    <span className="text-sm text-gray-400" data-id="element-66">ARS</span>
                  </div>
                </div>
                <p className="text-sage-200 text-sm mb-4" data-id="element-67">
                  Guías + Planillas + 4 Bonos
                </p>
                <Button variant="outline" fullWidth className="!text-white !border-white hover:!bg-white/20 text-base" data-id="element-68">
                  QUIERO MI MEGAPACK
                </Button>
              </div>

              {/* PRO Price Card */}
              <div className="bg-forest-600 rounded-xl p-6 border-2 border-sage-400 text-center relative shadow-xl transform md:scale-105" data-id="element-69">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-500 text-yellow-900 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-70">
                  ⭐ Más Popular
                </div>
                <h3 className="text-lg font-bold text-white uppercase tracking-wider mb-3 mt-2" data-id="element-71">
                  Megapack PRO
                </h3>
                <div className="mb-4" data-id="element-72">
                  <span className="text-sage-300 line-through text-lg block" data-id="element-73">
                    $59.990
                  </span>
                  <div className="flex items-baseline justify-center gap-1" data-id="element-74">
                    <span className="text-4xl md:text-5xl font-serif font-bold text-white" data-id="element-75">
                      $29.990
                    </span>
                    <span className="text-sm text-sage-200" data-id="element-76">ARS</span>
                  </div>
                </div>
                <p className="text-sage-100 text-sm mb-4" data-id="element-77">
                  Todo lo básico + Proveedores + Ventas
                </p>
                <Button fullWidth href="https://pagos.mundokrea.com/cart/47512247205940:1" className="text-lg py-5 shadow-xl shadow-forest-900/20" data-id="element-78">
                  QUIERO MI MEGAPACK PRO
                </Button>
              </div>
            </div>

            <div className="text-center mt-6 text-gray-400 text-xs md:text-sm flex flex-wrap justify-center items-center gap-3 md:gap-6" data-id="element-79">
              <span className="flex items-center gap-1" data-id="element-80">
                <ShieldCheck size={14} data-id="element-81" /> Pago Seguro
              </span>
              <span className="flex items-center gap-1" data-id="element-82">
                <Check size={14} data-id="element-83" /> Acceso Inmediato
              </span>
              <span className="flex items-center gap-1" data-id="element-84">
                <Star size={14} data-id="element-85" /> Garantía 7 Días
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 2. PROBLEM SECTION */}
      <section className="py-14 px-4 bg-white" data-id="element-86">
        <div className="max-w-4xl mx-auto" data-id="element-87">
          <SectionHeading subtitle="Si te sentís identificado/a con alguna de estas situaciones, seguí leyendo..." data-id="element-88">
            ¿Te Pasa Esto?
          </SectionHeading>

          <div className="grid gap-4" data-id="element-89">
            {['Tenés el patio lleno de plantas pero no sabés cómo multiplicarlas para vender sin que se mueran.', 'Querés generar un ingreso extra pero no tenés idea de qué precios poner ni cómo empezar.', 'Ves a otros vendiendo plantas en redes y ferias, y pensás: "¿Por qué yo no puedo?"', 'Perdés esquejes, gajos y semillas porque nadie te enseñó las técnicas correctas.', 'Anotás todo en papelitos, no sabés cuánto gastás ni cuánto ganás, y sentís que no avanzás.'].map((pain, index) => <FadeIn key={index} delay={index * 0.1} data-id="element-90">
                <div className="flex items-start gap-4 p-5 rounded-lg bg-red-50 border border-red-100" data-id="element-91">
                  <div className="bg-red-100 p-2 rounded-full flex-shrink-0 mt-0.5" data-id="element-92">
                    <X className="w-5 h-5 text-red-600" data-id="element-93" />
                  </div>
                  <p className="text-base text-gray-700 font-medium" data-id="element-94">{pain}</p>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.6} data-id="element-95">
            <div className="mt-10 text-center" data-id="element-96">
              <p className="text-lg text-forest-800 font-serif italic mb-5" data-id="element-97">
                "Si esto te suena familiar, no estás solo/a. Miles de personas
                aman las plantas pero no saben cómo convertir esa pasión en
                plata."
              </p>
              <Button variant="outline" data-id="element-98">VER LA SOLUCIÓN</Button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 3. BEFORE/AFTER SECTION */}
      <section className="py-14 px-4 bg-cream-100" data-id="element-99">
        <div className="max-w-6xl mx-auto" data-id="element-100">
          <SectionHeading centered data-id="element-101">Imaginá La Diferencia</SectionHeading>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mt-8" data-id="element-102">
            <FadeIn data-id="element-103">
              <div className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-red-400 h-full" data-id="element-104">
                <h3 className="text-2xl font-serif font-bold text-gray-800 mb-6 flex items-center gap-3" data-id="element-105">
                  <span className="bg-red-100 text-red-600 px-3 py-1 rounded text-sm font-sans uppercase tracking-wider" data-id="element-106">
                    Antes
                  </span>
                  Sin el Megapack
                </h3>
                <ul className="space-y-4" data-id="element-107">
                  {['Confusión total: no sabés qué sustrato usar ni cuándo regar', 'Perdés plata en plantas que se mueren o no prenden', 'Vendés de vez en cuando, sin un sistema claro', 'No tenés idea de tus costos reales ni tus ganancias', 'Te sentís estancado/a, sin saber cómo crecer'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-600" data-id="element-108">
                      <X className="w-5 h-5 text-red-400 flex-shrink-0" data-id="element-109" />
                      <span data-id="element-110">{item}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-111">
              <div className="bg-white p-8 rounded-2xl shadow-xl border-t-4 border-forest-600 h-full relative overflow-hidden" data-id="element-112">
                <div className="absolute top-0 right-0 w-24 h-24 bg-forest-50 rounded-bl-full -mr-4 -mt-4 z-0" data-id="element-113"></div>
                <h3 className="text-2xl font-serif font-bold text-forest-900 mb-6 flex items-center gap-3 relative z-10" data-id="element-114">
                  <span className="bg-forest-100 text-forest-700 px-3 py-1 rounded text-sm font-sans uppercase tracking-wider" data-id="element-115">
                    Después
                  </span>
                  Con el Megapack
                </h3>
                <ul className="space-y-4 relative z-10" data-id="element-116">
                  {['Claridad total: sabés exactamente qué hacer con cada planta', 'Tus esquejes prenden y tus plantas crecen sanas', 'Vendés con regularidad y confianza en tus precios', 'Controlás tus números con planillas profesionales', 'Sentís que tenés un negocio real, no un hobby'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-800 font-medium" data-id="element-117">
                      <div className="bg-green-100 rounded-full p-1" data-id="element-118">
                        <Check className="w-4 h-4 text-forest-600 flex-shrink-0" data-id="element-119" />
                      </div>
                      <span data-id="element-120">{item}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.4} data-id="element-121">
            <div className="mt-10 text-center" data-id="element-122">
              <Button data-id="element-123">QUIERO ESA TRANSFORMACIÓN</Button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 4. SOLUTION SECTION */}
      <section className="py-14 px-4 bg-white" data-id="element-124">
        <div className="max-w-7xl mx-auto" data-id="element-125">
          <div className="grid lg:grid-cols-2 gap-10 items-center" data-id="element-126">
            <FadeIn data-id="element-127">
              <div className="relative cursor-pointer" onClick={() => document.getElementById('comprar')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-128">
                <div className="absolute inset-0 bg-sage-200 rounded-3xl transform rotate-3" data-id="element-129"></div>
                <div className="relative bg-forest-900 rounded-3xl p-8 md:p-12 text-white shadow-2xl transform -rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-130">
                  <div className="border border-white/20 rounded-xl p-6 h-full flex flex-col items-center justify-center text-center min-h-[400px]" data-id="element-131">
                    <Sprout size={80} className="text-sage-300 mb-6" data-id="element-132" />
                    <h3 className="font-serif text-4xl font-bold mb-2" data-id="element-133">
                      MEGAPACK
                    </h3>
                    <p className="text-sage-200 text-xl tracking-widest uppercase mb-8" data-id="element-134">
                      Del Esqueje al Negocio
                    </p>
                    <div className="w-full h-px bg-gradient-to-r from-transparent via-white/30 to-transparent mb-8" data-id="element-135"></div>
                    <div className="grid grid-cols-2 gap-4 w-full text-sm text-gray-300" data-id="element-136">
                      <div className="bg-white/10 p-3 rounded" data-id="element-137">
                        📚 Guías PDF
                      </div>
                      <div className="bg-white/10 p-3 rounded" data-id="element-138">
                        📊 Planillas Excel
                      </div>
                      <div className="bg-white/10 p-3 rounded" data-id="element-139">
                        ✅ Checklists
                      </div>
                      <div className="bg-white/10 p-3 rounded" data-id="element-140">
                        📅 Calendarios
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-141">
              <div data-id="element-142">
                <span className="text-forest-600 font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-143">
                  Tu Sistema Completo
                </span>
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-forest-900 mb-6" data-id="element-144">
                  Presentamos: MEGAPACK <br data-id="element-145" />
                  Del Esqueje al Negocio
                </h2>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-146">
                  Todo lo que necesitás para pasar de "me gustan las plantas" a
                  "tengo un negocio rentable" — sin ser agrónomo, sin grandes
                  inversiones, y sin perder tiempo en prueba y error.
                </p>

                <div className="space-y-4 mb-10" data-id="element-147">
                  {['Aprendé las técnicas exactas de propagación que funcionan', 'Calculá tus costos y poné precios que te den ganancia real', 'Organizá tu producción mes a mes con calendarios listos', 'Usá plantillas profesionales para vender en redes sociales'].map((benefit, i) => <div key={i} className="flex items-center gap-3" data-id="element-148">
                      <Check className="w-6 h-6 text-sage-500 flex-shrink-0" data-id="element-149" />
                      <span className="text-lg text-gray-700" data-id="element-150">{benefit}</span>
                    </div>)}
                </div>

                <Button data-id="element-151">QUIERO MI MEGAPACK</Button>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 5. WHAT'S INCLUDED */}
      <section className="py-14 px-4 bg-cream-50" data-id="element-152">
        <div className="max-w-6xl mx-auto" data-id="element-153">
          <SectionHeading centered subtitle="Mirá todo lo que vas a recibir hoy mismo" data-id="element-154">
            ¿Qué Incluye El MEGAPACK?
          </SectionHeading>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-155">
            {[{
            icon: BookOpen,
            title: 'Guía Completa de Propagación',
            desc: '+50 técnicas paso a paso para multiplicar suculentas, plantas de interior y exterior. Con fotos y explicaciones claras.'
          }, {
            icon: Leaf,
            title: 'Manual de Cuidados por Especie',
            desc: 'Fichas técnicas de las plantas más vendidas: riego, luz, sustrato y tips para que crezcan perfectas.'
          }, {
            icon: Calendar,
            title: 'Planificador de Producción Anual',
            desc: 'Calendario mes a mes para saber qué sembrar, qué esquejar y cuándo tener stock listo para vender.'
          }, {
            icon: DollarSign,
            title: 'Calculadora de Precios y Costos',
            desc: 'Planilla Excel lista para usar. Poné tus números y sabé exactamente cuánto cobrar para ganar bien.'
          }, {
            icon: Share2,
            title: 'Plantillas para Redes Sociales',
            desc: 'Diseños editables en Canva para Instagram y Facebook. Publicá como un profesional sin saber diseño.'
          }, {
            icon: ListChecks,
            title: 'Checklist de Inicio Rápido',
            desc: 'Tu hoja de ruta de 7 días para arrancar tu emprendimiento. Sin vueltas, directo a la acción.'
          }].map((item, index) => <FadeIn key={index} delay={index * 0.1} data-id="element-156">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-sage-100 h-full cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1" onClick={() => document.getElementById('comprar')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-157">
                  <div className="w-12 h-12 bg-forest-50 rounded-full flex items-center justify-center text-forest-700 mb-4" data-id="element-158">
                    <item.icon size={24} data-id="element-159" />
                  </div>
                  <h3 className="text-lg font-serif font-bold text-forest-900 mb-2" data-id="element-160">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-161">
                    {item.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.7} data-id="element-162">
            <div className="mt-10 text-center" data-id="element-163">
              <Button data-id="element-164">QUIERO TODO ESTO</Button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 6. BONUSES SECTION - Editorial Mockup Display */}
      <section className="py-14 px-4 bg-cream-50" data-id="element-165">
        <div className="max-w-5xl mx-auto" data-id="element-166">
          <div className="text-center mb-10" data-id="element-167">
            <FadeIn data-id="element-168">
              <span className="text-forest-600 font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-169">
                Incluidos con tu compra
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-forest-900 mb-3" data-id="element-170">
                4 Guías de Regalo
              </h2>
              <div className="h-1 w-20 bg-sage-400 mt-4 mx-auto" data-id="element-171"></div>
            </FadeIn>
          </div>

          {/* Editorial 2x2 Grid of Real Mockups */}
          <FadeIn delay={0.2} data-id="element-172">
            <div className="grid grid-cols-2 gap-3 md:gap-5 max-w-3xl mx-auto" data-id="element-173">
              <div className="relative aspect-[3/4] overflow-hidden rounded-lg shadow-lg" data-id="element-174">
                <img src="https://cdn.magicpatterns.com/uploads/hZtNfrMKNwvg6Bc9gZ7tjH/mockup_Bonus_1_Enraizantes.png" alt="Guía Enraizantes Caseros" className="w-full h-full object-cover" data-id="element-175" />
                <div className="absolute top-3 left-3 bg-sage-100/90 backdrop-blur-sm text-forest-800 px-3 py-1.5 rounded text-[10px] md:text-xs font-medium tracking-wide uppercase border border-sage-200/50" data-id="element-176">
                  Bono de regalo
                </div>
              </div>
              <div className="relative aspect-[3/4] overflow-hidden rounded-lg shadow-lg" data-id="element-177">
                <img src="https://cdn.magicpatterns.com/uploads/kMog4eqme7roRMsqE8LUi2/Mockup_Bonus_2_Sustrato.png" alt="Guía Completa de Sustrato para Esquejes" className="w-full h-full object-cover" data-id="element-178" />
                <div className="absolute top-3 left-3 bg-sage-100/90 backdrop-blur-sm text-forest-800 px-3 py-1.5 rounded text-[10px] md:text-xs font-medium tracking-wide uppercase border border-sage-200/50" data-id="element-179">
                  Bono de regalo
                </div>
              </div>
              <div className="relative aspect-[3/4] overflow-hidden rounded-lg shadow-lg" data-id="element-180">
                <img src="https://cdn.magicpatterns.com/uploads/kHkKqFU8TzVckWTakpfimB/Mockup_Bonus_3_Suculenttas.png" alt="El Mundo de las Suculentas" className="w-full h-full object-cover" data-id="element-181" />
                <div className="absolute top-3 left-3 bg-sage-100/90 backdrop-blur-sm text-forest-800 px-3 py-1.5 rounded text-[10px] md:text-xs font-medium tracking-wide uppercase border border-sage-200/50" data-id="element-182">
                  Bono de regalo
                </div>
              </div>
              <div className="relative aspect-[3/4] overflow-hidden rounded-lg shadow-lg" data-id="element-183">
                <img src="https://cdn.magicpatterns.com/uploads/hNUUVMffrTaSym3AhSH34i/Mockup_Bonus_4_Errores_comunes.png" alt="No Cometas Estos Errores" className="w-full h-full object-cover" data-id="element-184" />
                <div className="absolute top-3 left-3 bg-sage-100/90 backdrop-blur-sm text-forest-800 px-3 py-1.5 rounded text-[10px] md:text-xs font-medium tracking-wide uppercase border border-sage-200/50" data-id="element-185">
                  Bono de regalo
                </div>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-186">
            <div className="mt-14 text-center" data-id="element-187">
              <Button className="text-lg px-10" data-id="element-188">
                QUIERO MI MEGAPACK + BONOS
              </Button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 7. TESTIMONIALS */}
      <section className="py-14 px-4 bg-white" data-id="element-189">
        <div className="max-w-6xl mx-auto" data-id="element-190">
          <SectionHeading centered subtitle="Mirá lo que dicen quienes ya lo tienen" data-id="element-191">
            Historias Reales de Argentina
          </SectionHeading>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-192">
            {[{
            quote: 'Gracias al Megapack pude organizar mi pequeño vivero en el patio. Ahora sé exactamente qué sembrar en cada época y mis ventas se duplicaron en 3 meses.',
            name: 'Silvia González',
            meta: '45 años, Córdoba',
            stars: 5
          }, {
            quote: 'La calculadora de precios me cambió todo. Estaba regalando mi trabajo sin darme cuenta. Ahora cobro lo justo y los clientes pagan contentos.',
            name: 'Roberto Méndez',
            meta: '52 años, Rosario',
            stars: 5
          }, {
            quote: 'Empecé sin saber nada, solo me gustaban las plantas. Las guías son tan claras que en 3 meses ya estaba vendiendo mis primeros esquejes en el barrio.',
            name: 'Ana Laura Paz',
            meta: '38 años, Mendoza',
            stars: 5
          }, {
            quote: 'Lo mejor es el calendario de producción. Antes improvisaba todo, ahora tengo un plan claro y siempre tengo plantas listas para vender.',
            name: 'Carlos Domínguez',
            meta: '48 años, Buenos Aires',
            stars: 5
          }, {
            quote: 'Las plantillas de redes sociales son geniales. Mis publicaciones ahora se ven profesionales y recibo muchos más mensajes de clientes.',
            name: 'María Elena Ruiz',
            meta: '41 años, Tucumán',
            stars: 5
          }].map((testi, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-193">
                <div className="bg-cream-50 p-6 rounded-xl border border-sage-100 relative h-full flex flex-col" data-id="element-194">
                  <div className="flex gap-1 mb-3" data-id="element-195">
                    {[...Array(testi.stars)].map((_, s) => <Star key={s} size={14} className="fill-yellow-400 text-yellow-400" data-id="element-196" />)}
                  </div>
                  <p className="text-gray-700 italic mb-4 leading-relaxed text-sm flex-grow" data-id="element-197">
                    "{testi.quote}"
                  </p>
                  <div className="flex items-center gap-3" data-id="element-198">
                    <div className="w-9 h-9 bg-sage-200 rounded-full flex items-center justify-center text-forest-800 font-bold font-serif text-sm" data-id="element-199">
                      {testi.name.charAt(0)}
                    </div>
                    <div data-id="element-200">
                      <p className="font-bold text-forest-900 text-sm" data-id="element-201">
                        {testi.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-202">{testi.meta}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* 8. FAQ SECTION */}
      <section className="py-14 px-4 bg-gray-50" data-id="element-203">
        <div className="max-w-3xl mx-auto" data-id="element-204">
          <SectionHeading centered data-id="element-205">Preguntas Frecuentes</SectionHeading>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 md:p-6" data-id="element-206">
            <AccordionItem question="¿Necesito experiencia previa con plantas?" answer="No, para nada. El Megapack está pensado para principiantes. Explicamos todo desde cero, con lenguaje simple y muchos ejemplos. Si sabés regar una planta, podés seguir las guías." data-id="element-207" />
            <AccordionItem question="¿Cómo recibo el material?" answer="El acceso es 100% digital e inmediato. Apenas confirmás el pago, recibís un email con el link de descarga. Podés bajarlo a tu compu, tablet o celular y usarlo cuando quieras, incluso sin internet." data-id="element-208" />
            <AccordionItem question="¿Por cuánto tiempo tengo acceso?" answer="¡Para siempre! Una vez que comprás, el material es tuyo de por vida. Además, si hacemos actualizaciones, te las enviamos gratis." data-id="element-209" />
            <AccordionItem question="¿Qué pasa si no me sirve? ¿Hay garantía?" answer="Sí, tenés 7 días de garantía total. Si por cualquier motivo sentís que no es para vos, nos escribís y te devolvemos el 100% de tu dinero. Sin preguntas, sin vueltas." data-id="element-210" />
            <AccordionItem question="¿Puedo pagar en cuotas?" answer="Sí, a través de Mercado Pago podés pagar con tarjeta de crédito en cuotas. Las opciones dependen de tu banco, pero generalmente hay hasta 12 cuotas disponibles." data-id="element-211" />
            <AccordionItem question="¿Sirve para cualquier tipo de planta?" answer="El Megapack cubre las plantas más populares y rentables: suculentas, cactus, plantas de interior, aromáticas y algunas de exterior. Son las que más se venden en Argentina." data-id="element-212" />
          </div>
        </div>
      </section>

      {/* 9. GUARANTEE SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-213">
        <div className="max-w-4xl mx-auto" data-id="element-214">
          <FadeIn data-id="element-215">
            <div className="border-2 border-dashed border-forest-200 bg-forest-50/50 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 text-center md:text-left" data-id="element-216">
              <div className="flex-shrink-0" data-id="element-217">
                <ShieldCheck size={100} className="text-forest-600" strokeWidth={1} data-id="element-218" />
              </div>
              <div data-id="element-219">
                <h3 className="text-2xl md:text-3xl font-serif font-bold text-forest-900 mb-4" data-id="element-220">
                  Garantía Total de 7 Días
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-221">
                  Probá el Megapack completo durante 7 días. Leé las guías, usá
                  las planillas, aplicá las técnicas. Si no estás 100%
                  satisfecho/a con tu compra, te devolvemos cada centavo.
                </p>
                <p className="text-forest-700 font-bold" data-id="element-222">
                  El riesgo es todo nuestro. Vos solo tenés que ganar.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* MEGAPACK PRO EXCLUSIVE SECTION */}
      <section className="py-14 px-4 bg-white relative overflow-hidden" data-id="element-223">
        {/* Botanical accent decorations */}
        <div className="absolute top-10 left-10 w-32 h-32 opacity-5" data-id="element-224">
          <Leaf className="w-full h-full text-forest-800 rotate-45" data-id="element-225" />
        </div>
        <div className="absolute bottom-10 right-10 w-48 h-48 opacity-5" data-id="element-226">
          <Leaf className="w-full h-full text-forest-800 -rotate-12" data-id="element-227" />
        </div>
        <div className="absolute top-1/2 left-0 w-24 h-24 opacity-5" data-id="element-228">
          <Sprout className="w-full h-full text-sage-600" data-id="element-229" />
        </div>

        <div className="max-w-5xl mx-auto relative z-10" data-id="element-230">
          <div className="text-center mb-10" data-id="element-231">
            <FadeIn data-id="element-232">
              <span className="bg-forest-600 text-white px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-3 inline-block" data-id="element-233">
                🔥 Solo por hoy
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-forest-900 mb-3" data-id="element-234">
                Además de todo esto… solo por hoy podés llevar el MEGAPACK PRO
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-235">
                Pensado para quienes quieren vender en serio y profesionalizar
                su proyecto.
              </p>
              <div className="h-1 w-20 bg-sage-400 mt-5 mx-auto" data-id="element-236"></div>
            </FadeIn>
          </div>

          {/* 2x2 Grid of PRO Product Mockups */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6 max-w-3xl mx-auto" data-id="element-237">
            {/* CARD 1: Guía de Proveedores */}
            <FadeIn delay={0} data-id="element-238">
              <div className="group cursor-pointer h-full" onClick={() => document.getElementById('comprar')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-239">
                <div className="relative bg-forest-900 rounded-2xl shadow-lg border border-forest-800 overflow-hidden transition-all duration-300 group-hover:shadow-2xl group-hover:-translate-y-2 p-6" data-id="element-240">
                  {/* Subtle Texture */}
                  <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px]" data-id="element-241"></div>

                  {/* Top Left Label */}
                  <div className="absolute top-4 left-4 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold tracking-wide z-10 shadow-sm" data-id="element-242">
                    PRO EXCLUSIVO
                  </div>

                  {/* Product Image - Centered Focal Point */}
                  <div className="relative z-0 flex justify-center items-center pt-10 pb-6" data-id="element-243">
                    <img src="https://cdn.magicpatterns.com/uploads/fC6dMhQrBHPfq1R7t2Es9A/guia_proveedores_.png" alt="Guía de Proveedores Argentina 2026" className="w-full max-w-[200px] h-auto object-contain drop-shadow-2xl" data-id="element-244" />
                  </div>

                  {/* Text Content - Single Title + Subtitle */}
                  <div className="relative z-0 text-center pb-8" data-id="element-245">
                    <h3 className="font-serif text-xl font-bold text-white mb-2 leading-tight" data-id="element-246">
                      Guía de Proveedores Argentina 2026
                    </h3>
                    <p className="text-sm text-sage-300" data-id="element-247">
                      Contactos verificados de mayoristas y viveros
                    </p>
                  </div>

                  {/* Bottom Left Label */}
                  <div className="absolute bottom-4 left-4 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white/80" data-id="element-248">
                    DIGITAL
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* CARD 2: Estrategia y Ventas */}
            <FadeIn delay={0.1} data-id="element-249">
              <div className="group cursor-pointer h-full" onClick={() => document.getElementById('comprar')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-250">
                <div className="relative bg-forest-900 rounded-2xl shadow-lg border border-forest-800 overflow-hidden transition-all duration-300 group-hover:shadow-2xl group-hover:-translate-y-2 p-6" data-id="element-251">
                  {/* Subtle Texture */}
                  <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px]" data-id="element-252"></div>

                  {/* Top Left Label */}
                  <div className="absolute top-4 left-4 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold tracking-wide z-10 shadow-sm" data-id="element-253">
                    PRO EXCLUSIVO
                  </div>

                  {/* Product Image - Centered Focal Point */}
                  <div className="relative z-0 flex justify-center items-center pt-10 pb-6" data-id="element-254">
                    <img src="https://cdn.magicpatterns.com/uploads/rrzh9J99MDiNdBwQyHdxba/estrategia_y_ventas.png" alt="Módulo de Estrategia y Ventas" className="w-full max-w-[200px] h-auto object-contain drop-shadow-2xl" data-id="element-255" />
                  </div>

                  {/* Text Content - Single Title + Subtitle */}
                  <div className="relative z-0 text-center pb-8" data-id="element-256">
                    <h3 className="font-serif text-xl font-bold text-white mb-2 leading-tight" data-id="element-257">
                      Módulo de Estrategia y Ventas
                    </h3>
                    <p className="text-sm text-sage-300" data-id="element-258">
                      Técnicas probadas para vender más y mejor
                    </p>
                  </div>

                  {/* Bottom Left Label */}
                  <div className="absolute bottom-4 left-4 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white/80" data-id="element-259">
                    DIGITAL
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* CARD 3: Calendario */}
            <FadeIn delay={0.2} data-id="element-260">
              <div className="group cursor-pointer h-full" onClick={() => document.getElementById('comprar')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-261">
                <div className="relative bg-forest-900 rounded-2xl shadow-lg border border-forest-800 overflow-hidden transition-all duration-300 group-hover:shadow-2xl group-hover:-translate-y-2 p-6" data-id="element-262">
                  {/* Subtle Texture */}
                  <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px]" data-id="element-263"></div>

                  {/* Top Left Label */}
                  <div className="absolute top-4 left-4 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold tracking-wide z-10 shadow-sm" data-id="element-264">
                    PRO EXCLUSIVO
                  </div>

                  {/* Product Image - Centered Focal Point */}
                  <div className="relative z-0 flex justify-center items-center pt-10 pb-6" data-id="element-265">
                    <img src="https://cdn.magicpatterns.com/uploads/6w6B6NmivDU7EQezsmXMcv/mockup_calendario_.png" alt="Calendario de Propagación por Esquejes" className="w-full max-w-[200px] h-auto object-contain drop-shadow-2xl" data-id="element-266" />
                  </div>

                  {/* Text Content - Single Title + Subtitle */}
                  <div className="relative z-0 text-center pb-8" data-id="element-267">
                    <h3 className="font-serif text-xl font-bold text-white mb-2 leading-tight" data-id="element-268">
                      Calendario de Propagación
                    </h3>
                    <p className="text-sm text-sage-300" data-id="element-269">
                      Plantilla editable mes a mes
                    </p>
                  </div>

                  {/* Bottom Left Label */}
                  <div className="absolute bottom-4 left-4 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white/80" data-id="element-270">
                    DIGITAL
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* CARD 4: Stickers y Tarjetas */}
            <FadeIn delay={0.3} data-id="element-271">
              <div className="group cursor-pointer h-full" onClick={() => document.getElementById('comprar')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-272">
                <div className="relative bg-forest-900 rounded-2xl shadow-lg border border-forest-800 overflow-hidden transition-all duration-300 group-hover:shadow-2xl group-hover:-translate-y-2 p-6" data-id="element-273">
                  {/* Subtle Texture */}
                  <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px]" data-id="element-274"></div>

                  {/* Top Left Label */}
                  <div className="absolute top-4 left-4 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold tracking-wide z-10 shadow-sm" data-id="element-275">
                    PRO EXCLUSIVO
                  </div>

                  {/* Product Image - Centered Focal Point */}
                  <div className="relative z-0 flex justify-center items-center pt-10 pb-6" data-id="element-276">
                    <img src="https://cdn.magicpatterns.com/uploads/ti9H8GGA1eWLYy7j9YdKZU/¨sticker_2_Emprende_hoy.png" alt="Stickers y Tarjetas para Emprendedores" className="w-full max-w-[200px] h-auto object-contain drop-shadow-2xl" data-id="element-277" />
                  </div>

                  {/* Text Content - Single Title + Subtitle */}
                  <div className="relative z-0 text-center pb-8" data-id="element-278">
                    <h3 className="font-serif text-xl font-bold text-white mb-2 leading-tight" data-id="element-279">
                      Stickers y Tarjetas Editables
                    </h3>
                    <p className="text-sm text-sage-300" data-id="element-280">
                      Diseños listos para imprimir
                    </p>
                  </div>

                  {/* Bottom Left Label */}
                  <div className="absolute bottom-4 left-4 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white/80" data-id="element-281">
                    DIGITAL
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* Bottom text */}
          <FadeIn delay={0.5} data-id="element-282">
            <div className="mt-10 text-center" data-id="element-283">
              <div className="inline-flex items-center gap-3 bg-sage-50 border border-sage-200 rounded-full px-5 py-3" data-id="element-284">
                <Sprout className="w-5 h-5 text-forest-600" data-id="element-285" />
                <span className="text-forest-800 font-medium" data-id="element-286">
                  Todo esto se suma a lo que ya incluye el MEGAPACK BÁSICO.
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 10. PRICING SECTION */}
      <section id="comprar" className="py-24 px-4 bg-gray-900 text-white relative overflow-hidden" data-id="element-287">
        {/* Abstract background shapes */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-forest-900/50 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" data-id="element-288"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-forest-800/30 rounded-full blur-3xl -ml-20 -mb-20 pointer-events-none" data-id="element-289"></div>

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-290">
          <SectionHeading centered dark subtitle="Elegí la opción que mejor se adapte a tus objetivos" data-id="element-291">
            Elegí Tu Plan
          </SectionHeading>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-stretch" data-id="element-292">
            {/* CARD 1 - BASIC */}
            <FadeIn delay={0.1} data-id="element-293">
              <div className="bg-white text-gray-900 rounded-2xl p-8 border border-gray-200 relative overflow-hidden h-full flex flex-col" data-id="element-294">
                <h3 className="text-xl font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-295">
                  Megapack Básico
                </h3>
                <div className="flex items-baseline gap-2 mb-2" data-id="element-296">
                  <span className="text-gray-400 line-through text-lg" data-id="element-297">
                    $39.990
                  </span>
                </div>
                <div className="flex items-baseline gap-2 mb-6" data-id="element-298">
                  <span className="text-4xl font-serif font-bold text-gray-900" data-id="element-299">
                    $19.990
                  </span>
                  <span className="text-sm text-gray-500" data-id="element-300">ARS</span>
                </div>

                <div className="space-y-4 mb-8 flex-grow" data-id="element-301">
                  <div className="flex items-center gap-3" data-id="element-302">
                    <Check className="w-5 h-5 text-forest-600" data-id="element-303" />
                    <span data-id="element-304">Guía Completa de Propagación</span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-305">
                    <Check className="w-5 h-5 text-forest-600" data-id="element-306" />
                    <span data-id="element-307">Manual de Cuidados por Especie</span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-308">
                    <Check className="w-5 h-5 text-forest-600" data-id="element-309" />
                    <span data-id="element-310">Planificador de Producción</span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-311">
                    <Check className="w-5 h-5 text-forest-600" data-id="element-312" />
                    <span data-id="element-313">Calculadora de Precios</span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-314">
                    <Check className="w-5 h-5 text-forest-600" data-id="element-315" />
                    <span data-id="element-316">Plantillas de Redes Sociales</span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-317">
                    <Check className="w-5 h-5 text-forest-600" data-id="element-318" />
                    <span data-id="element-319">Checklist de Inicio Rápido</span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-320">
                    <Check className="w-5 h-5 text-forest-600" data-id="element-321" />
                    <span data-id="element-322">4 Bonos Exclusivos</span>
                  </div>
                </div>

                <Button variant="outline" fullWidth className="text-base" data-id="element-323">
                  QUIERO MI MEGAPACK
                </Button>
              </div>
            </FadeIn>

            {/* CARD 2 - PRO */}
            <FadeIn delay={0.2} data-id="element-324">
              <div className="bg-white text-gray-900 rounded-2xl p-8 border-4 border-forest-500 relative transform md:scale-105 shadow-2xl h-full flex flex-col" data-id="element-325">
                <div className="absolute top-0 left-0 w-full bg-forest-600 text-white text-center py-2 text-sm font-bold uppercase tracking-widest" data-id="element-326">
                  ⭐ Más Popular
                </div>

                <div className="mt-8" data-id="element-327">
                  <h3 className="text-xl font-bold text-forest-700 uppercase tracking-wider mb-2" data-id="element-328">
                    Megapack PRO
                  </h3>
                  <div className="flex items-baseline gap-2 mb-2" data-id="element-329">
                    <span className="text-gray-400 line-through text-lg" data-id="element-330">
                      $59.990
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-6" data-id="element-331">
                    <span className="text-5xl font-serif font-bold text-forest-900" data-id="element-332">
                      $29.990
                    </span>
                    <span className="text-sm text-gray-500" data-id="element-333">ARS</span>
                  </div>

                  <div className="space-y-4 mb-8 flex-grow" data-id="element-334">
                    <p className="font-bold text-sm text-forest-800 uppercase border-b border-gray-100 pb-2" data-id="element-335">
                      ✅ Todo lo del Básico más:
                    </p>

                    <div className="flex items-center gap-3 bg-forest-50 p-3 rounded" data-id="element-336">
                      <Check className="w-5 h-5 text-forest-600" data-id="element-337" />
                      <span className="font-medium" data-id="element-338">
                        Guía de Proveedores Argentina 2026
                      </span>
                    </div>
                    <div className="flex items-center gap-3 bg-forest-50 p-3 rounded" data-id="element-339">
                      <Check className="w-5 h-5 text-forest-600" data-id="element-340" />
                      <span className="font-medium" data-id="element-341">
                        Módulo de Estrategia y Ventas
                      </span>
                    </div>
                    <div className="flex items-center gap-3 bg-forest-50 p-3 rounded" data-id="element-342">
                      <Check className="w-5 h-5 text-forest-600" data-id="element-343" />
                      <span className="font-medium" data-id="element-344">
                        Calendario de Propagación Avanzado
                      </span>
                    </div>
                    <div className="flex items-center gap-3 bg-forest-50 p-3 rounded" data-id="element-345">
                      <Check className="w-5 h-5 text-forest-600" data-id="element-346" />
                      <span className="font-medium" data-id="element-347">
                        Kit de Tarjetas + Stickers Editables
                      </span>
                    </div>
                  </div>

                  <Button fullWidth className="text-lg py-5 shadow-xl shadow-forest-900/20" data-id="element-348">
                    QUIERO MI MEGAPACK PRO
                  </Button>
                </div>
              </div>
            </FadeIn>
          </div>

          <div className="text-center mt-12 text-gray-400 text-sm flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8" data-id="element-349">
            <span className="flex items-center gap-2" data-id="element-350">
              <ShieldCheck size={16} data-id="element-351" /> Pago 100% Seguro
            </span>
            <span className="flex items-center gap-2" data-id="element-352">
              <Check size={16} data-id="element-353" /> Acceso Inmediato
            </span>
            <span className="flex items-center gap-2" data-id="element-354">
              <Star size={16} data-id="element-355" /> Garantía de 7 Días
            </span>
          </div>

          <div className="flex justify-center items-center mt-8 opacity-70 grayscale hover:grayscale-0 transition-all duration-300 text-[16px] gap-[16px]" data-id="element-356">
            <img src="https://cdn.magicpatterns.com/uploads/1HQciX1ikAJfg1ue2ARJ4A/0_hero_paginas-de-brandbook-mercado-pago_1020--1020x701--ad6db02e--rebrand-mp.webp" alt="Mercado Pago" className="h-12 object-contain bg-white rounded px-2" data-id="element-357" />
            <img src="https://cdn.magicpatterns.com/uploads/osuwgDR7tKMQRXDrYQhwot/visa.webp" alt="Visa" className="h-12 object-contain bg-white rounded px-2" data-id="element-358" />
            <img src="https://cdn.magicpatterns.com/uploads/hULoc4emKMM7PG3cwGQegR/master.webp" alt="Mastercard" className="h-12 object-contain bg-white rounded px-2" data-id="element-359" />
          </div>

          <p className="text-center mt-6 text-gray-500 text-sm" data-id="element-360">
            Pago único • Acceso de por vida • Sin suscripciones
          </p>
        </div>
      </section>

      {/* 11. FINAL CTA */}
      <section className="py-20 px-4 bg-forest-800 text-white text-center" data-id="element-361">
        <div className="max-w-3xl mx-auto" data-id="element-362">
          <FadeIn data-id="element-363">
            <h2 className="text-3xl md:text-5xl font-serif font-bold mb-6" data-id="element-364">
              Tu Negocio de Plantas Empieza Hoy
            </h2>
            <p className="text-xl text-sage-100 mb-6 leading-relaxed" data-id="element-365">
              Dejá de postergar. Dejá de improvisar. Tenés todo lo que necesitás
              para empezar: el plan, las herramientas y la guía paso a paso.
            </p>
            <p className="text-lg text-sage-200 mb-10" data-id="element-366">
              Solo falta tu decisión.
            </p>
            <Button variant="secondary" className="px-12 py-6 text-xl shadow-2xl" data-id="element-367">
              QUIERO MI MEGAPACK <ArrowRight className="ml-2" data-id="element-368" />
            </Button>
            <p className="mt-6 text-sm text-sage-300" data-id="element-369">
              ⏰ Oferta válida por tiempo limitado. Los bonos pueden desaparecer
              en cualquier momento.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-forest-950 text-gray-500 py-12 px-4 text-center text-sm" data-id="element-370">
        <div className="max-w-6xl mx-auto" data-id="element-371">
          <div className="flex items-center justify-center gap-2 mb-6 text-sage-600" data-id="element-372">
            <Sprout size={24} data-id="element-373" />
            <span className="font-serif text-xl font-bold" data-id="element-374">
              Del Esqueje al Negocio
            </span>
          </div>
          <p className="mb-4" data-id="element-375">
            &copy; 2026 MEGAPACK Del Esqueje al Negocio. Todos los derechos
            reservados.
          </p>
          <div className="flex justify-center gap-6" data-id="element-376">
            <a href="#" className="hover:text-gray-300 transition-colors" data-id="element-377">
              Términos y Condiciones
            </a>
            <a href="#" className="hover:text-gray-300 transition-colors" data-id="element-378">
              Política de Privacidad
            </a>
            <a href="#" className="hover:text-gray-300 transition-colors" data-id="element-379">
              Contacto
            </a>
          </div>
        </div>
      </footer>
    </div>;
}